<template>
	<v-menu
		ref="menu"
		offset-y
		:close-on-content-click="false"
		max-width="300"
	>
		<template v-slot:activator="{ on, attr }">
			<v-text-field
				v-on="on"
				readonly
				v-bind="{ ...bindProps, ...inputProps, ...attr }"
				:value="localValue"
				:append-icon="clearable ? 'mdi-close' : null"
				@click:append="$emit('click:clear')"
			>
				<template v-slot:prepend-inner>
					<v-icon :color="localValue">mdi-circle</v-icon>
				</template>
			</v-text-field>
		</template>
		<v-card class="pa-1">
			<v-color-picker
				v-model="color"
				mode="hexa"
				canvas-height="120"
				v-bind="bindProps"
				@input="localValue = $event?.hexa"
			/>
			<v-card-actions class="pt-0 pb-1 px-1 justify-end">
				<v-btn
					small
					color="primary"
					class="rounded-lg"
					@click="$refs.menu.save(localValue)"
				>
					{{$t('close')}}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script>
export default {
	name: 'VColorInput',

	props: {
		label: { type: String },
		dense: { type: Boolean },
		clearable: { type: Boolean },
		readonly: { type: Boolean },
		outlined: { type: Boolean },
		inputProps: { type: Object, default: () => ({}) },
		'hide-details': { type: [Boolean, String] },
		rules: { type: Array },
		value: { type: String }
	},

	data() {
		return {
			color: null
		}
	},

	watch: {
		value: {
			handler(val) {
				if (typeof val === 'string' && /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/m.test(val)) {
					this.color = this.convertHexaToColorCodes(val)
				} else {
					this.color = null
				}
			},
			immediate: true
		}
	},

	computed: {
		localValue: {
			get() {
				return this.value
			},
			set(val) {
				this.$emit('input', val);
			}
		},
		bindProps() {
			return {
				label: this.label,
				dense: this.dense,
				readonly: this.readonly,
				outlined: this.outlined,
				'hide-details': this.hideDetails,
				rules: this.rules,
				class: 'rounded-medium '
			}
		},
	},

	methods: {
		convertHexaToColorCodes(hexaColorCode) {
			const hex = hexaColorCode.match(/[a-fA-F0-9]{2}/g);
			const r = parseInt(hex[0], 16);
			const g = parseInt(hex[1], 16);
			const b = parseInt(hex[2], 16);
			const a = hex.length === 8 ? parseInt(hex[3], 16) / 255 : 1;

			// Convert RGBA to HSVA
			const rNorm = r / 255;
			const gNorm = g / 255;
			const bNorm = b / 255;

			const max = Math.max(rNorm, gNorm, bNorm);
			const min = Math.min(rNorm, gNorm, bNorm);
			const delta = max - min;

			let h = 0;
			if (delta !== 0) {
				if (max === rNorm) {
					h = 60 * (((gNorm - bNorm) / delta) % 6);
				} else if (max === gNorm) {
					h = 60 * (((bNorm - rNorm) / delta) + 2);
				} else {
					h = 60 * (((rNorm - gNorm) / delta) + 4);
				}
			}
			if (h < 0) h += 360;

			const s = max === 0 ? 0 : delta / max;
			const v = max;

			// Convert HSVA to HSLA
			const l = (2 - s) * v / 2;
			const sHsla = l === 0 || l === 1 ? 0 : (v - l) / Math.min(l, 1 - l);

			return {
				alpha: a,
				hex: `#${hexaColorCode.slice(0, 6).toUpperCase()}`,
				hexa: `#${hexaColorCode.toUpperCase()}`,
				rgba: { r, g, b, a },
				hsva: { h: Math.round(h), s: parseFloat(s.toFixed(3)), v: parseFloat(v.toFixed(3)), a },
				hsla: { h: Math.round(h), s: parseFloat(sHsla.toFixed(3)), l: parseFloat(l.toFixed(3)), a },
				hue: Math.round(h)
			};
		}
	}
}
</script>

<style>

</style>